import React from 'react';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import type { ScheduleCover } from '../../services/tenantApi';

export type CoverBucket = 'active' | 'upcoming' | 'past';

export function coverBucket(cover: Pick<ScheduleCover, 'status' | 'startUtc' | 'endUtc'>, now: Date = new Date()): CoverBucket {
  const nowMs = now.getTime();
  if (String(cover.status).toLowerCase() !== 'active' || Date.parse(cover.endUtc) <= nowMs) return 'past';
  return Date.parse(cover.startUtc) <= nowMs ? 'active' : 'upcoming';
}

export function categorizeCovers(covers: ScheduleCover[], now: Date = new Date()): Record<CoverBucket, ScheduleCover[]> {
  const buckets: Record<CoverBucket, ScheduleCover[]> = { active: [], upcoming: [], past: [] };
  for (const cover of covers || []) buckets[coverBucket(cover, now)].push(cover);
  const byStart = (a: ScheduleCover, b: ScheduleCover) => Date.parse(a.startUtc) - Date.parse(b.startUtc);
  buckets.active.sort(byStart);
  buckets.upcoming.sort(byStart);
  buckets.past.sort((a, b) => Date.parse(b.endUtc) - Date.parse(a.endUtc));
  return buckets;
}

/** Timeline tooltip text for a cover segment: "E covering B – reason". */
export function describeCoverSegment(
  cover: Pick<ScheduleCover, 'coveredUserId' | 'coveredUserName' | 'replacementUserId' | 'replacementUserName' | 'reason'> | undefined,
  resolveUserLabel: (userId: string | null | undefined) => string,
): string {
  if (!cover) return 'cover';
  const replacement = cover.replacementUserName || resolveUserLabel(cover.replacementUserId);
  const covered = cover.coveredUserName || resolveUserLabel(cover.coveredUserId);
  const reason = (cover.reason || '').trim();
  return `${replacement} covering ${covered}${reason ? ` – ${reason}` : ''}`;
}

const BUCKET_TITLES: Record<CoverBucket, string> = { active: 'Active', upcoming: 'Upcoming', past: 'Past' };

export const ScheduleCoversPanel: React.FC<{
  covers: ScheduleCover[];
  timezone: string;
  rotationNameById: Map<string, string>;
  resolveUserLabel: (userId: string | null | undefined) => string;
  canManage: boolean;
  busyCoverId?: string | null;
  onAdd: () => void;
  onHelp?: () => void;
  onEnd: (cover: ScheduleCover) => void;
  onCancel: (cover: ScheduleCover) => void;
  now?: Date;
}> = ({ covers, timezone, rotationNameById, resolveUserLabel, canManage, busyCoverId, onAdd, onHelp, onEnd, onCancel, now }) => {
  const buckets = categorizeCovers(covers, now);
  const fmt = (iso?: string | null) => {
    if (!iso) return '—';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return formatDateTime(date, { timeZone: timezone || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };
  const rotationLabel = (cover: ScheduleCover) =>
    cover.rotationId ? (rotationNameById.get(cover.rotationId) || 'Rotation') : 'All rotations';

  return (
    <div className="data-table schedule-covers-panel" style={{ padding: '1rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Rotation covers</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {onHelp && <button type="button" className="btn-secondary" onClick={onHelp}>Cover HowTo</button>}
        <button
          type="button"
          className="btn-secondary"
          onClick={onAdd}
          disabled={!canManage}
          title={canManage ? 'Replace an operator for whole rotation blocks' : 'Only schedule managers or team leads can add covers'}
        >Add cover</button>
        </div>
      </div>
      {covers.length === 0 ? (
        <div className="empty-row">No covers for this schedule.</div>
      ) : (['active', 'upcoming', 'past'] as CoverBucket[]).map(bucket => buckets[bucket].length > 0 && (
        <div key={bucket} className={`cover-bucket cover-bucket-${bucket}`} style={{ marginBottom: 10 }}>
          <h4 style={{ margin: '0.25rem 0' }}>{BUCKET_TITLES[bucket]} ({buckets[bucket].length})</h4>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>Covered</th>
                  <th>Replacement</th>
                  <th>Rotation</th>
                  <th>From ({timezone})</th>
                  <th>To</th>
                  <th>Reason</th>
                  <th>{bucket === 'past' ? 'Status' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {buckets[bucket].map(cover => (
                  <tr key={cover.coverId}>
                    <td>{cover.coveredUserName || resolveUserLabel(cover.coveredUserId)}</td>
                    <td>{cover.replacementUserName || resolveUserLabel(cover.replacementUserId)}</td>
                    <td>{rotationLabel(cover)}</td>
                    <td>{fmt(cover.startUtc)}</td>
                    <td>{fmt(cover.endUtc)}</td>
                    <td>{cover.reason || '—'}</td>
                    <td>
                      {bucket === 'active' && (
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ padding: '0.15rem 0.45rem' }}
                          disabled={!canManage || busyCoverId === cover.coverId}
                          onClick={() => onEnd(cover)}
                        >End now</button>
                      )}
                      {bucket === 'upcoming' && (
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ padding: '0.15rem 0.45rem' }}
                          disabled={!canManage || busyCoverId === cover.coverId}
                          onClick={() => onCancel(cover)}
                        >Cancel</button>
                      )}
                      {bucket === 'past' && <span className="help-text">{cover.status}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};
